import { useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../api';
import { Button, Card, EmptyState, Input, Loading, Money, PageHeader, Select, Table } from '../ui';
import type {
  FormalProductMaster,
  ImportBatchRecord,
  ProductFamilyItem,
} from '../shared/types';
import { HUI_SHENG_CATEGORY_MAP, parseProductMasterTxt, type ParsedTxtRow } from '../shared/txt-parser';
import type { Notify } from '../pages';

const SAMPLE_TXT_DATA = `item_id\tcname\tename\tmea\tmcs_no\tmcs_name\tcust_id\tsname\titem_iid\titem_id2\tstock\ton_order\tsa_price\tavg_cost\tin_price\tvip_price\temp_price\treturn_yn\tupdtime
001001\t惠生維他命C發泡錠 20錠\tHui-Sheng Vitamin C Effervescent 20s\t管\tB22\t一般皮膚炎\tV001\t惠生生醫股份有限公司\t4710001001018\t\t150\t20\t280.00\t140.00\t150.00\t250.00\t220.00\tY\t2026-08-01 10:00:00
001002\t惠生金盞花葉黃素膠囊 60粒\tHui-Sheng Lutein 60s\t瓶\tM31\t健康食品類\tV001\t惠生生醫股份有限公司\t4710001001025\t\t80\t0\t890.00\t450.00\t480.00\t799.00\t700.00\tY\t2026-08-01 10:00:00
001003\t高單位益生菌粉末 30包\tProbiotics Daily Powder 30s\t盒\tM31\t健康食品類\tV002\t健康大師生技\t4710002001015\t\t200\t50\t650.00\t310.00\t330.00\t580.00\t500.00\tY\t2026-08-01 10:00:00
002001\t日本進口醫療用滅菌紗布 5片裝\tSterile Gauze Pad 5s\t包\tJ12\t醫療耗材與敷料\tV003\t台灣醫療耗材\t4710003001012\t\t500\t0\t50.00\t20.00\t22.00\t45.00\t38.00\tY\t2026-08-01 10:00:00
002002\t醫用透氣膠帶 1吋×10碼\tMedical Porous Tape 1inch\t卷\tJ12\t醫療耗材與敷料\tV003\t台灣醫療耗材\t4710003001029\t\t-15\t0\t65.00\t28.00\t30.00\t55.00\t48.00\tY\t2026/6/16 10:03:16:29
003001\t深層舒緩護手霜 75ml\tDeep Moisture Hand Cream 75ml\t支\tK05\t美粧護理\tV004\t法系美妝進口商\t4710004001019\t\t60\t0\t350.00\t170.00\t180.00\t320.00\t280.00\tY\t2025/12/22 08:45:03:553
003002\t溫和氨基酸潔顏慕斯 150ml\tAmino Cleansing Mousse 150ml\t瓶\tK05\t美粧護理\tV004\t法系美妝進口商\t4710004001026\t\t45\t15\t480.00\t230.00\t250.00\t420.00\t370.00\tY\t2021/5/1 20:48:10
004001\t健保處方藥調劑服務費\tDispensing Fee\t次\tFEE01\t服務項目\t\t\t\t\t0\t0\t0.00\t0.00\t0.00\t0.00\t0.00\tN\t2026-08-01 10:00:00
005001\t特約門市購物提袋 (大)\tShopping Bag (L)\t個\tPKG01\t耗材包裝\tV005\t印刷包裝材料行\t\t\t1200\t0\t2.00\t0.80\t0.00\t2.00\t2.00\tN\t2026-08-01 10:00:00`;

export function FormalProductMastersPage({ notify }: { notify: Notify }) {
  const [activeTab, setActiveTab] = useState<'masters' | 'import' | 'history' | 'quality'>(
    'masters',
  );

  // 商品主檔狀態
  const [items, setItems] = useState<FormalProductMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [categoryCode, setCategoryCode] = useState('');
  const [subcategoryCode, setSubcategoryCode] = useState('');
  const [recordType, setRecordType] = useState('');
  const [qualityFilter, setQualityFilter] = useState('');
  const [missingFilter, setMissingFilter] = useState('');
  const [total, setTotal] = useState(0);

  // 編輯自訂欄位 Modal
  const [editingItem, setEditingItem] = useState<FormalProductMaster | null>(null);
  const [giveawayTerms, setGiveawayTerms] = useState('');
  const [ingredientsJson, setIngredientsJson] = useState('');
  const [familyId, setFamilyId] = useState('');
  const [families, setFamilies] = useState<ProductFamilyItem[]>([]);
  const [savingCustom, setSavingCustom] = useState(false);

  // 匯入 TXT 狀態 (極簡化 3 步)
  const [txtContent, setTxtContent] = useState('');
  const [fileName, setFileName] = useState('');
  const encoding = 'CP950';
  const [taxType, setTaxType] = useState<'TAX_INCLUSIVE' | 'TAX_EXCLUSIVE'>('TAX_INCLUSIVE');
  const [isDragging, setIsDragging] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<{
    file_name: string;
    file_hash?: string;
    previous_import_warning?: string | null;
    total_rows: number;
    importable_rows: number;
    warning_rows: number;
    invalid_rows_count: number;
    invalid_rows: Array<{ rowNumber: number; itemId?: string; cname?: string; reason: string }>;
    breakdown: {
      inserted: number;
      updated: number;
      unchanged: number;
      price_changed: number;
      high_risk_price: number;
      identity_conflicts: number;
    };
    summary: Record<string, number>;
    header_columns: string[];
    preview_rows: Array<Record<string, any>>;
  } | null>(null);
  const [showInvalidModal, setShowInvalidModal] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ percent: number; text: string } | null>(null);
  const [parsedValidRows, setParsedValidRows] = useState<ParsedTxtRow[]>([]);
  const [lastImportResult, setLastImportResult] = useState<any | null>(null);

  // 匯入紀錄狀態
  const [batches, setBatches] = useState<ImportBatchRecord[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  // 品質中心 5 大子頁籤狀態
  const [qualityActiveTab, setQualityActiveTab] = useState<string>('price_changes');
  const [qualitySummary, setQualitySummary] = useState<Record<string, number>>({});
  const [qualityItems, setQualityItems] = useState<any[]>([]);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [batchActionLoading, setBatchActionLoading] = useState(false);

  // 成分自動補全狀態
  const [enrichmentSummary, setEnrichmentSummary] = useState<{
    total: number;
    notSearched: number;
    searching: number;
    foundUnconfirmed: number;
    confirmed: number;
    notFound: number;
    ambiguous: number;
    identityUnconfirmed: number;
    missingIngredients: number;
  } | null>(null);
  const [enrichingSingleId, setEnrichingSingleId] = useState<string | null>(null);
  const [showBatchEnrichModal, setShowBatchEnrichModal] = useState(false);
  const [batchEnriching, setBatchEnriching] = useState(false);
  const [batchEnrichProgress, setBatchEnrichProgress] = useState('');
  const [batchCategory, setBatchCategory] = useState('');
  const [batchEnrichResults, setBatchEnrichResults] = useState<any[]>([]);

  // 候選成分抽屜/Modal
  const [selectedCandidateProduct, setSelectedCandidateProduct] =
    useState<FormalProductMaster | null>(null);
  const [candidateDrawerData, setCandidateDrawerData] = useState<{
    product: any;
    sources: any[];
    ingredients: any[];
  } | null>(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [confirmingSourceId, setConfirmingSourceId] = useState<string | null>(null);
  const [rejectingSourceId, setRejectingSourceId] = useState<string | null>(null);

  // 載入成分補全統計總覽
  const loadEnrichmentSummary = async () => {
    try {
      const res = await api.productEnrichment.getSummary();
      setEnrichmentSummary(res.summary);
    } catch {
      // ignore
    }
  };

  // 載入商品主檔
  const loadItems = async (qFilter = qualityFilter, mFilter = missingFilter) => {
    setLoading(true);
    try {
      const res = await api.productMasters.list({
        keyword,
        category_code: categoryCode,
        subcategory_code: subcategoryCode,
        record_type: recordType,
        quality_filter: qFilter,
        missing_filter: mFilter,
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
      void loadEnrichmentSummary();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '載入商品主檔失敗，請稍後再試。'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 單品成分查找
  const handleSingleEnrich = async (productId: string) => {
    if (!productId) {
      notify('商品資料不完整，無法執行成分查找。', 'error');
      return;
    }
    setEnrichingSingleId(productId);
    try {
      const res = await api.productEnrichment.enrichSingle(productId);
      notify(res?.message || '已完成成分查找');
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '成分查找失敗，請稍後再試。'), 'error');
    } finally {
      setEnrichingSingleId(null);
    }
  };

  // 批次補全商品成分
  const handleBatchEnrich = async () => {
    setBatchEnriching(true);
    setBatchEnrichProgress('正在比對官方快取與可信來源資料庫中…');
    try {
      const res = await api.productEnrichment.enrichBatch({
        limit: 50,
        categoryCode: batchCategory || undefined,
      });
      setBatchEnrichResults(res?.items || []);
      notify(
        res?.message ||
          `批次補全完成！處理 ${res?.totalProcessed || 0} 筆，成功查得 ${res?.enrichedCount || 0} 筆，查無資料 ${res?.notFoundCount || 0} 筆。`,
      );
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '批次補全成分失敗，請稍後再試。'), 'error');
    } finally {
      setBatchEnriching(false);
      setBatchEnrichProgress('');
    }
  };

  // 開啟候選成分抽屜
  const openCandidateDrawer = async (item: FormalProductMaster) => {
    if (!item?.id) {
      notify('商品資料不完整。', 'error');
      return;
    }
    setSelectedCandidateProduct(item);
    setLoadingCandidates(true);
    try {
      const res = await api.productEnrichment.getCandidates(item.id);
      setCandidateDrawerData(res);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '讀取候選成分失敗，請稍後再試。'), 'error');
    } finally {
      setLoadingCandidates(false);
    }
  };

  // 採購確認成分資料
  const handleConfirmSource = async (sourceId?: string) => {
    if (!selectedCandidateProduct?.id) return;
    setConfirmingSourceId(sourceId || 'DIRECT');
    try {
      const res = await api.productEnrichment.confirm(selectedCandidateProduct.id, { sourceId });
      notify(res?.message || '已成功確認成分資料！');
      const updated = await api.productEnrichment.getCandidates(selectedCandidateProduct.id);
      setCandidateDrawerData(updated);
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '確認成分資料失敗，請稍後再試。'), 'error');
    } finally {
      setConfirmingSourceId(null);
    }
  };

  // 排除錯誤來源
  const handleRejectSource = async (sourceId: string) => {
    if (!selectedCandidateProduct?.id || !sourceId) return;
    setRejectingSourceId(sourceId);
    try {
      const res = await api.productEnrichment.rejectSource(selectedCandidateProduct.id, sourceId);
      notify(res?.message || '已排除該來源');
      const updated = await api.productEnrichment.getCandidates(selectedCandidateProduct.id);
      setCandidateDrawerData(updated);
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '排除來源失敗，請稍後再試。'), 'error');
    } finally {
      setRejectingSourceId(null);
    }
  };

  useEffect(() => {
    api.productFamilies
      .list()
      .then((res) => setFamilies(res.items))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'masters') {
      void loadItems();
    } else if (activeTab === 'history') {
      void loadBatches();
    } else if (activeTab === 'quality') {
      setSelectedIds([]);
      void loadQualityCenter(qualityActiveTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // 載入品質中心資料
  const loadQualityCenter = async (tab = qualityActiveTab) => {
    setQualityLoading(true);
    setSelectedIds([]);
    try {
      const res = await api.productMasters.getDataQualityTabs(tab);
      setQualitySummary(res.summary);
      setQualityItems(res.items);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '載入資料待確認中心失敗'), 'error');
    } finally {
      setQualityLoading(false);
    }
  };

  // 載入匯入紀錄
  const loadBatches = async () => {
    setBatchesLoading(true);
    try {
      const res = await api.productMasters.importBatches();
      setBatches(res.items);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '載入匯入紀錄失敗'), 'error');
    } finally {
      setBatchesLoading(false);
    }
  };

  // 批次處理品質中心項目
  const handleBatchResolve = async (action: string) => {
    if (selectedIds.length === 0) {
      notify('請先勾選要批次處理的項目。', 'error');
      return;
    }
    setBatchActionLoading(true);
    try {
      const res = await api.productMasters.batchResolveQuality(qualityActiveTab, action, selectedIds);
      notify(`已成功批次處理 ${res.resolved_count} 筆項目！`);
      setSelectedIds([]);
      void loadQualityCenter(qualityActiveTab);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '批次處理失敗'), 'error');
    } finally {
      setBatchActionLoading(false);
    }
  };

  // 全選 / 取消全選
  const handleToggleSelectAll = () => {
    if (selectedIds.length === qualityItems.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(qualityItems.map((item) => item.id));
    }
  };

  // 單選
  const handleToggleSelect = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((x) => x !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // 回復批次 (Rollback)
  const handleRollbackBatch = async (batchId: string, batchNo: string) => {
    if (!confirm(`確定要回復批次 ${batchNo} 嗎？這將還原該批次所修改的來源價格與暫存紀錄。`)) return;
    try {
      await api.productMasters.rollbackBatch(batchId);
      notify(`批次 ${batchNo} 已成功回復！`);
      void loadBatches();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '回復批次失敗'), 'error');
    }
  };

  // 重複商品審核操作
  const handleResolveDuplicate = async (candidateId: string, action: string, notes?: string) => {
    try {
      await api.productMasters.resolveDuplicate(candidateId, action, notes);
      notify('重複商品決策已成功儲存！');
      void loadQualityCenter(qualityActiveTab);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '儲存重複商品決策失敗'), 'error');
    }
  };

  // 價格異動審核操作
  const handleResolvePriceReview = async (
    productId: string,
    decision: 'ACCEPT_NEW_PRICE' | 'RETAIN_OLD_PRICE',
    newPrice?: number,
  ) => {
    try {
      await api.productMasters.resolvePriceReview(productId, decision, newPrice);
      notify(decision === 'ACCEPT_NEW_PRICE' ? '已接受新進價！' : '已保留原進價！');
      void loadQualityCenter(qualityActiveTab);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '審核價格異動失敗'), 'error');
    }
  };

  // 開啟編輯自訂欄位 Modal
  const openEditModal = (item: FormalProductMaster) => {
    setEditingItem(item);
    setGiveawayTerms(item.current_vendor_giveaway_terms || '');
    setIngredientsJson(item.ingredients_json || '');
    setFamilyId(item.product_family_id || '');
  };

  // 儲存自訂欄位
  const saveCustomFields = async () => {
    if (!editingItem) return;
    setSavingCustom(true);
    try {
      await api.productMasters.updateCustomFields(editingItem.id, {
        current_vendor_giveaway_terms: giveawayTerms,
        ingredients_json: ingredientsJson,
        product_family_id: familyId || null,
      });
      notify('商品主檔自訂欄位已儲存！');
      setEditingItem(null);
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '儲存自訂欄位失敗，請稍後再試。'), 'error');
    } finally {
      setSavingCustom(false);
    }
  };

  // 執行 TXT 解析預覽 (檢查檔案) - 本地極速無延遲解析
  const executePreview = (content: string, fName: string, enc: string) => {
    if (!content.trim()) {
      notify('請先選擇或貼上 TXT 檔案內容。', 'error');
      return;
    }
    setPreviewLoading(true);
    try {
      const res = parseProductMasterTxt(content, { encoding: enc });
      if (res.missingRequiredHeaders && res.missingRequiredHeaders.length > 0) {
        notify(`缺少必要 Header 欄位：${res.missingRequiredHeaders.join('、')}，請檢查檔案。`, 'error');
        setPreviewData(null);
        setParsedValidRows([]);
        return;
      }
      setParsedValidRows(res.validRows);
      setPreviewData({
        file_name: fName,
        total_rows: res.totalRows,
        importable_rows: res.importableRows,
        warning_rows: res.warningRows,
        invalid_rows_count: res.invalidRowsCount,
        invalid_rows: res.invalidRows,
        breakdown: {
          inserted: res.importableRows,
          updated: 0,
          unchanged: 0,
          price_changed: 0,
          high_risk_price: 0,
          identity_conflicts: 0,
        },
        summary: res.summary,
        header_columns: res.headerColumns,
        preview_rows: res.validRows.slice(0, 50),
      });
      notify(`檔案檢查完成！共讀取 ${res.totalRows.toLocaleString()} 筆商品，${res.importableRows.toLocaleString()} 筆可匯入。`);
    } catch (err: unknown) {
      notify(`解析 TXT 失敗：${getApiErrorMessage(err, '檔案格式無法解析')}`, 'error');
      setPreviewData(null);
      setParsedValidRows([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  // 處理檔案選取與讀取 (CP950 / UTF-8 解碼)
  const processFile = async (file: File) => {
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      let decoded = '';
      try {
        const decoder = new TextDecoder(encoding === 'CP950' ? 'big5' : 'utf-8');
        decoded = decoder.decode(buffer);
      } catch {
        const decoder = new TextDecoder('utf-8');
        decoded = decoder.decode(buffer);
      }
      setTxtContent(decoded);
      executePreview(decoded, file.name, encoding);
    } catch (err: unknown) {
      notify(`檔案讀取失敗：${getApiErrorMessage(err, '無法讀取檔案內容')}`, 'error');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    void processFile(file);
  };

  // 拖曳上傳
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void processFile(file);
    }
  };

  // 載入範例資料
  const handleLoadSample = () => {
    setFileName('sample_huisheng_master.txt');
    setTxtContent(SAMPLE_TXT_DATA);
    executePreview(SAMPLE_TXT_DATA, 'sample_huisheng_master.txt', encoding);
  };

  // 提交 TXT 匯入 (採用分塊上傳 Chunk Ingestion，支援超大檔案且不逾時)
  const handleCommitTxt = async () => {
    if (!parsedValidRows || parsedValidRows.length === 0) {
      notify('無可匯入之商品資料，請先選擇或檢查檔案。', 'error');
      return;
    }
    setCommitting(true);
    setImportProgress({ percent: 5, text: '正在初始化匯入批次...' });

    try {
      // 1. 初始化批次
      const startRes = await api.productMasters.startImport({
        file_name: fileName || 'import.txt',
        encoding,
        tax_type: taxType,
        total_rows: previewData?.total_rows || parsedValidRows.length,
        warning_rows: previewData?.warning_rows || 0,
        invalid_rows_count: previewData?.invalid_rows_count || 0,
        summary: previewData?.summary,
      });

      const batchId = startRes.batch_id;
      const batchNo = startRes.batch_no;

      let totalInserted = 0;
      let totalUpdated = 0;
      let totalUnchanged = 0;
      let totalPriceChanges = 0;

      // 2. 分塊上傳 (每批 50 筆，平穩傳輸且不超載)
      const chunkSize = 50;
      const totalChunks = Math.ceil(parsedValidRows.length / chunkSize);

      for (let cIdx = 0; cIdx < totalChunks; cIdx++) {
        const chunk = parsedValidRows.slice(cIdx * chunkSize, (cIdx + 1) * chunkSize);
        const chunkRes = await api.productMasters.importChunk({
          batch_id: batchId,
          tax_type: taxType,
          chunk,
        });

        totalInserted += chunkRes.inserted_count;
        totalUpdated += chunkRes.updated_count;
        totalUnchanged += chunkRes.unchanged_count;
        totalPriceChanges += chunkRes.price_change_count;

        const currentProcessed = Math.min((cIdx + 1) * chunkSize, parsedValidRows.length);
        const percent = 5 + Math.round((currentProcessed / parsedValidRows.length) * 85);
        setImportProgress({
          percent,
          text: `正在匯入資料：${currentProcessed.toLocaleString()} / ${parsedValidRows.length.toLocaleString()} 筆 (${percent}%)...`,
        });
      }

      // 3. 完成批次總結
      setImportProgress({ percent: 95, text: '正在計算重複候選與更新主檔最新狀態...' });
      const allImportedCodes = parsedValidRows.map((r) => r.item_id).filter(Boolean);

      const finishRes = await api.productMasters.finishImport({
        batch_id: batchId,
        inserted_count: totalInserted,
        updated_count: totalUpdated,
        unchanged_count: totalUnchanged,
        price_change_count: totalPriceChanges,
        all_imported_codes: allImportedCodes,
        sample_for_duplicates: parsedValidRows.slice(0, 150),
      });

      const finalResult = {
        success: true,
        batch_id: batchId,
        batch_no: batchNo,
        tax_type: taxType,
        total_rows: previewData?.total_rows || parsedValidRows.length,
        importable_rows: parsedValidRows.length,
        inserted_rows: totalInserted,
        updated_rows: totalUpdated,
        unchanged_rows: totalUnchanged,
        warning_rows: previewData?.warning_rows || 0,
        invalid_rows_count: previewData?.invalid_rows_count || 0,
        invalid_rows: previewData?.invalid_rows || [],
        price_change_count: totalPriceChanges,
        duplicate_candidate_count: finishRes.duplicate_candidate_count,
      };

      setLastImportResult(finalResult);
      notify(
        `🎉 匯入完成！成功處理 ${parsedValidRows.length.toLocaleString()} 筆商品（新增 ${totalInserted.toLocaleString()}、更新 ${totalUpdated.toLocaleString()}、未變 ${totalUnchanged.toLocaleString()}）。`,
      );
      setPreviewData(null);
      setParsedValidRows([]);
      setTxtContent('');
      void loadItems();
    } catch (err: unknown) {
      notify(`匯入失敗：${getApiErrorMessage(err, '伺服器處理失敗，請稍後再試。')}`, 'error');
    } finally {
      setCommitting(false);
      setImportProgress(null);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <PageHeader
        eyebrow="HUI-SHENG MASTER DATA"
        title="惠生正式商品主檔"
        description="管理惠生既有商品主檔、極簡 TXT 上傳匯入、大分類自動推導、來源層與人工層隔離保護及商品成分自動補全中心。"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowBatchEnrichModal(true)}
              className="border-teal-300 text-teal-800 bg-teal-50/60 hover:bg-teal-100/60 font-bold"
            >
              ⚡ 批次自動補全成分
              {enrichmentSummary && enrichmentSummary.missingIngredients > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-teal-700 text-white text-[10px]">
                  缺 {enrichmentSummary.missingIngredients.toLocaleString()}
                </span>
              )}
            </Button>
            <Button
              variant={activeTab === 'import' ? 'primary' : 'secondary'}
              onClick={() => setActiveTab('import')}
              className={activeTab === 'import' ? 'bg-teal-700' : ''}
            >
              📥 上傳商品主檔 TXT
            </Button>
            <Button
              variant={activeTab === 'quality' ? 'primary' : 'secondary'}
              onClick={() => setActiveTab('quality')}
              className={activeTab === 'quality' ? 'bg-teal-700' : ''}
            >
              🩺 資料待確認中心
            </Button>
          </div>
        }
      />

      {/* 4 個主頁籤 */}
      <div className="flex border-b border-slate-200">
        <button
          className={`px-5 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'masters'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => setActiveTab('masters')}
        >
          🗂 正式商品主檔 {total > 0 && `(${total})`}
        </button>
        <button
          className={`px-5 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'import'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => setActiveTab('import')}
        >
          📥 上傳惠生商品主檔 TXT
        </button>
        <button
          className={`px-5 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'quality'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => setActiveTab('quality')}
        >
          🩺 資料待確認中心
        </button>
        <button
          className={`px-5 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'history'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => setActiveTab('history')}
        >
          📜 匯入紀錄與 Rollback
        </button>
      </div>

      {/* TAB 1: 正式商品主檔列表 */}
      {activeTab === 'masters' && (
        <div className="space-y-6">
          <Card className="p-4 bg-slate-50 border-slate-200">
            <div className="grid gap-3 md:grid-cols-6">
              <div className="md:col-span-2">
                <Input
                  label="關鍵字搜尋"
                  placeholder="搜尋商品代號、品名、條碼、廠商…"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </div>
              <Select
                label="大分類 (A~P)"
                value={categoryCode}
                onChange={(e) => setCategoryCode(e.target.value)}
              >
                <option value="">全部大分類</option>
                {Object.entries(HUI_SHENG_CATEGORY_MAP).map(([code, name]) => (
                  <option key={code} value={code}>
                    {code} - {name}
                  </option>
                ))}
              </Select>
              <Input
                label="子分類代碼 (mcs_no)"
                placeholder="例：B22, M31"
                value={subcategoryCode}
                onChange={(e) => setSubcategoryCode(e.target.value)}
              />
              <Select
                label="類型過濾"
                value={recordType}
                onChange={(e) => setRecordType(e.target.value)}
              >
                <option value="">全部品項</option>
                <option value="PRODUCT">實體商品 (PRODUCT)</option>
                <option value="FEE">手續費/服務費 (FEE)</option>
                <option value="SERVICE">勞務項目 (SERVICE)</option>
                <option value="VIRTUAL">虛擬品項 (VIRTUAL)</option>
                <option value="NEEDS_REVIEW">待確認 (NEEDS_REVIEW)</option>
              </Select>
              <div className="flex items-end gap-2">
                <Button onClick={() => void loadItems()} className="flex-1 bg-teal-700">
                  🔍 查詢
                </Button>
                {(qualityFilter || missingFilter) && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setQualityFilter('');
                      setMissingFilter('');
                      void loadItems('', '');
                    }}
                  >
                    清除篩選
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {loading ? (
            <Loading label="載入商品主檔中…" />
          ) : items.length === 0 ? (
            <Card className="p-8 text-center bg-slate-50 border-dashed border-2 border-slate-200">
              <div className="max-w-md mx-auto space-y-4">
                <div className="text-4xl">📦</div>
                <h3 className="text-lg font-bold text-ink">目前尚無符合之商品主檔資料</h3>
                <p className="text-xs text-slate-500">
                  可使用 TXT 檔案直接匯入，或點擊下方按鈕前往匯入。
                </p>
                <div className="flex justify-center gap-3 pt-2">
                  <Button onClick={() => setActiveTab('import')} className="bg-teal-700 font-bold">
                    前往上傳 TXT
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex justify-between items-center text-xs text-slate-500">
                <span>共找到 {total} 筆商品主檔</span>
                <span className="text-teal-700 font-medium">💡 支援「自動補全商品成分」及「官方藥品許可證與健食功效成分比對」</span>
              </div>
              <Table>
                <thead>
                  <tr className="bg-slate-50 text-slate-600 text-xs">
                    <th className="p-3">商品代號</th>
                    <th className="p-3">品名與規格</th>
                    <th className="p-3">大分類 / 子分類</th>
                    <th className="p-3">商品成分與來源</th>
                    <th className="p-3">目前進價 (稅別)</th>
                    <th className="p-3">VIP價 / 售價</th>
                    <th className="p-3">庫存 / 在途</th>
                    <th className="p-3">廠商搭贈</th>
                    <th className="p-3">狀態標籤</th>
                    <th className="p-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/50">
                      <td className="p-3 font-mono font-bold text-teal-800">
                        {item.product_code}
                      </td>
                      <td className="p-3">
                        <div className="font-bold text-ink">{item.product_name}</div>
                        {item.english_name && (
                          <div className="text-[11px] text-slate-400">{item.english_name}</div>
                        )}
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          單位：{item.sales_unit || '未設定'} ｜ 條碼：
                          {item.primary_barcode_candidate || '無'}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-bold rounded text-[11px]">
                          {item.product_category_code
                            ? `${item.product_category_code} ${HUI_SHENG_CATEGORY_MAP[item.product_category_code] || ''}`
                            : '未設定'}
                        </span>
                        <div className="text-[11px] text-slate-500 mt-1">
                          子分類：{item.product_subcategory_code || item.imported_subcategory_code || '—'}{' '}
                          {item.imported_subcategory_name || ''}
                        </div>
                      </td>
                      <td className="p-3 max-w-[200px]">
                        {item.ingredient_status === 'CONFIRMED' ? (
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold border border-emerald-200 text-[10px]">
                                ✓ 已確認
                              </span>
                              {item.ingredient_source_name && (
                                <span className="text-[10px] text-teal-700 truncate max-w-[110px]" title={item.ingredient_source_name}>
                                  [{item.ingredient_source_name}]
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-600 truncate mt-0.5" title={item.ingredient_raw_text || ''}>
                              {item.ingredient_raw_text || '已正式建立'}
                            </div>
                          </div>
                        ) : item.ingredient_status === 'FOUND_UNCONFIRMED' ? (
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 font-bold border border-amber-200 text-[10px]">
                                △ 待確認 ({Math.round(item.ingredient_confidence_score || 0)}%)
                              </span>
                            </div>
                            <div className="text-[11px] text-slate-600 truncate mt-0.5" title={item.ingredient_raw_text || ''}>
                              {item.ingredient_raw_text}
                            </div>
                          </div>
                        ) : item.ingredient_status === 'AMBIGUOUS' ? (
                          <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 font-bold border border-rose-200 text-[10px]">
                            ! 來源衝突
                          </span>
                        ) : item.ingredient_status === 'IDENTITY_UNCONFIRMED' ? (
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold text-[10px]">
                            ? 身分不明
                          </span>
                        ) : item.ingredient_status === 'NOT_FOUND' ? (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px]">
                            — 找不到
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 text-[10px]">
                            ○ 未查找
                          </span>
                        )}
                      </td>
                      <td className="p-3 font-bold text-ink">
                        <div>
                          {item.current_purchase_price === 0 || item.current_purchase_price === null ? (
                            <span className="text-amber-700 font-bold">未設定 (0元)</span>
                          ) : (
                            <Money value={item.current_purchase_price} />
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 font-normal">
                          {item.purchase_price_tax_type === 'TAX_EXCLUSIVE' ? '未稅' : '含稅'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="font-bold text-teal-700">
                          VIP: <Money value={item.vip_price} />
                        </div>
                        <div className="text-slate-400 text-[11px]">
                          售: <Money value={item.regular_sale_price} />
                        </div>
                      </td>
                      <td className="p-3">
                        <div className={`font-bold ${item.warehouse_stock < 0 ? 'text-rose-600' : 'text-slate-700'}`}>
                          庫存: {item.warehouse_stock}
                        </div>
                        <div className="text-slate-400 text-[11px]">在途: {item.on_order_quantity}</div>
                      </td>
                      <td className="p-3">
                        {item.current_vendor_giveaway_terms ? (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded text-[11px]">
                            🎁 {item.current_vendor_giveaway_terms}
                          </span>
                        ) : item.detected_giveaway_candidate ? (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-[11px]">
                            候選: {item.detected_giveaway_candidate}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {item.status_hint === 'INACTIVE' && (
                            <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded text-[10px] font-bold">
                              已停用
                            </span>
                          )}
                          {item.missing_from_latest_import === 1 && (
                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px]">
                              未出現 ({item.missing_import_count}次)
                            </span>
                          )}
                          {item.sku_identity_conflict_flag === 1 && (
                            <span className="px-1.5 py-0.5 bg-rose-200 text-rose-900 rounded text-[10px] font-bold">
                              身分衝突
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => void handleSingleEnrich(item.id)}
                            loading={enrichingSingleId === item.id}
                            disabled={Boolean(enrichingSingleId)}
                            className="text-xs px-2 py-1 min-h-7"
                            title="依國際條碼/許可證自動查找官方與可信成分"
                          >
                            🔍 查找成分
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => void openCandidateDrawer(item)}
                            className="text-xs px-2 py-1 min-h-7 bg-teal-50 hover:bg-teal-100 text-teal-800"
                            title="查看候選來源與確認"
                          >
                            📋 候選
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(item)}
                            className="text-xs px-2 py-1 min-h-7 text-slate-500"
                          >
                            ✏️
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
        </div>
      )}

      {/* TAB 2: 上傳惠生商品主檔 TXT (極簡化 3 步) */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          {/* 匯入完成摘要 (Section 二十七) */}
          {lastImportResult && (
            <Card className="p-6 bg-emerald-50/70 border-emerald-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-base font-bold text-emerald-900">
                    🎉 匯入完成（批號：{lastImportResult.batch_no}）
                  </h3>
                  <p className="text-xs text-emerald-700 mt-1">
                    進價稅別：{lastImportResult.tax_type === 'TAX_EXCLUSIVE' ? '未稅' : '含稅'}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setLastImportResult(null)}>
                  ✕ 關閉
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center mb-4">
                <div className="p-3 bg-white rounded-xl border border-emerald-200">
                  <span className="text-[11px] text-slate-500 block">成功處理</span>
                  <span className="text-xl font-extrabold text-ink">{lastImportResult.importable_rows} 筆</span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-emerald-200">
                  <span className="text-[11px] text-emerald-600 block">新增</span>
                  <span className="text-xl font-extrabold text-emerald-700">+{lastImportResult.inserted_rows}</span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-sky-200">
                  <span className="text-[11px] text-sky-600 block">更新</span>
                  <span className="text-xl font-extrabold text-sky-700">~{lastImportResult.updated_rows}</span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-slate-200">
                  <span className="text-[11px] text-slate-500 block">無變更</span>
                  <span className="text-xl font-extrabold text-slate-600">{lastImportResult.unchanged_rows || 0}</span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-amber-200">
                  <span className="text-[11px] text-amber-600 block">需注意 / 待確認</span>
                  <span className="text-xl font-extrabold text-amber-700">{lastImportResult.warning_rows || 0}</span>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={() => setActiveTab('masters')} className="bg-teal-700 text-xs">
                  🗂 查看商品主檔
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setActiveTab('quality');
                    void loadQualityCenter('price_changes');
                  }}
                  className="text-xs"
                >
                  🩺 查看需注意資料 ({lastImportResult.warning_rows || 0})
                </Button>
              </div>
            </Card>
          )}

          {/* 上傳主區塊 (Section 二、三、三十三) */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-base font-bold text-ink">上傳惠生商品主檔 TXT</h3>
                <p className="text-xs text-slate-500 mt-1">
                  請選取惠生 POS / ERP 匯出之商品主檔 TXT 檔案，系統將自動辨識編碼並執行安全比對與更新。
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={handleLoadSample} className="text-xs">
                ⚡ 載入示範 TXT 測試資料
              </Button>
            </div>

            {/* 【1】選擇檔案 */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${
                isDragging
                  ? 'border-teal-500 bg-teal-50/50 scale-[1.01]'
                  : 'border-slate-300 hover:border-teal-400 bg-slate-50/50'
              }`}
              onClick={() => document.getElementById('file-upload-input')?.click()}
            >
              <input
                id="file-upload-input"
                type="file"
                accept=".txt,.tsv,.csv"
                className="hidden"
                onChange={handleFileUpload}
              />
              <div className="max-w-md mx-auto space-y-2">
                <div className="text-4xl">📄</div>
                <div className="text-sm font-bold text-ink">
                  {fileName ? `已選取檔案：${fileName}` : '點擊或將惠生商品主檔 TXT 檔案拖曳至此'}
                </div>
                <p className="text-xs text-slate-400">支援副檔名：.txt, .tsv (最大 20MB)</p>
              </div>
            </div>

            {/* 【2】選擇目前進價稅別 (Section 二) */}
            <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <label className="block text-xs font-bold text-slate-700 mb-2">
                目前進價稅別（必填）：
              </label>
              <div className="flex gap-6 text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="taxType"
                    value="TAX_INCLUSIVE"
                    checked={taxType === 'TAX_INCLUSIVE'}
                    onChange={() => setTaxType('TAX_INCLUSIVE')}
                  />
                  <span className="font-bold text-ink">目前進價為含稅 (預設)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="taxType"
                    value="TAX_EXCLUSIVE"
                    checked={taxType === 'TAX_EXCLUSIVE'}
                    onChange={() => setTaxType('TAX_EXCLUSIVE')}
                  />
                  <span className="font-bold text-ink">目前進價為未稅</span>
                </label>
              </div>
            </div>

            {/* 【3】檢查檔案按鈕 */}
            <div className="flex justify-end gap-3 mt-6">
              {txtContent && (
                <Button
                  onClick={() => void executePreview(txtContent, fileName, encoding)}
                  loading={previewLoading}
                  className="bg-teal-700 hover:bg-teal-800 text-white font-bold text-sm px-6 py-2.5 shadow"
                >
                  🔍 檢查檔案
                </Button>
              )}
            </div>
          </Card>

          {/* 檔案檢查完成後之簡單摘要 (Section 四、五、三十三) */}
          {previewLoading ? (
            <Loading label="系統自動檢查檔案與比對商品中…" />
          ) : previewData ? (
            <Card className="p-6 space-y-6 bg-slate-50/50 border-slate-200">
              {previewData.previous_import_warning && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                  ℹ️ {previewData.previous_import_warning}
                </div>
              )}

              <div>
                <h4 className="text-base font-bold text-ink">檢查完成摘要</h4>
                <p className="text-xs text-slate-500 mt-0.5">檔案名稱：{previewData.file_name}</p>
              </div>

              {/* 核心大數字摘要 (Section 四 & 三十三) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-white rounded-2xl border border-slate-200">
                  <span className="text-xs text-slate-500 block font-bold">讀取商品</span>
                  <span className="text-2xl font-extrabold text-ink mt-1 block">
                    {previewData.total_rows.toLocaleString()} 筆
                  </span>
                </div>

                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                  <span className="text-xs text-emerald-800 block font-bold">✓ 可匯入</span>
                  <span className="text-2xl font-extrabold text-emerald-700 mt-1 block">
                    {previewData.importable_rows.toLocaleString()} 筆
                  </span>
                </div>

                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
                  <span className="text-xs text-amber-800 block font-bold">△ 需注意 (有警示)</span>
                  <span className="text-2xl font-extrabold text-amber-700 mt-1 block">
                    {previewData.warning_rows.toLocaleString()} 筆
                  </span>
                </div>

                <div className="p-4 bg-rose-50 rounded-2xl border border-rose-200">
                  <span className="text-xs text-rose-800 block font-bold">✕ 無法匯入</span>
                  <span className="text-2xl font-extrabold text-rose-700 mt-1 block">
                    {previewData.invalid_rows_count.toLocaleString()} 筆
                  </span>
                </div>
              </div>

              {/* 細項拆解 */}
              <div className="p-4 bg-white rounded-xl border border-slate-200 flex flex-wrap gap-4 text-xs">
                <span className="text-emerald-700 font-bold">
                  新增：{previewData.breakdown.inserted.toLocaleString()}
                </span>
                <span className="text-sky-700 font-bold">
                  更新：{previewData.breakdown.updated.toLocaleString()}
                </span>
                <span className="text-slate-500">
                  資料未變：{previewData.breakdown.unchanged.toLocaleString()}
                </span>
                <span className="text-amber-700 font-bold">
                  價格異動：{previewData.breakdown.price_changed.toLocaleString()}
                </span>
                <span className="text-rose-700 font-bold">
                  高風險價格：{previewData.breakdown.high_risk_price.toLocaleString()}
                </span>
              </div>

              {/* 匯入即時進度條 */}
              {importProgress && (
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-xl space-y-2">
                  <div className="flex justify-between text-xs font-bold text-teal-900">
                    <span>{importProgress.text}</span>
                    <span>{importProgress.percent}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-teal-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress.percent}%` }}
                    />
                  </div>
                </div>
              )}

              {/* 按鈕組 (Section 五) */}
              <div className="flex flex-wrap justify-between items-center gap-3 pt-2">
                <div>
                  {previewData.invalid_rows_count > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowInvalidModal(true)}
                      className="text-rose-600 hover:bg-rose-50 text-xs font-bold"
                    >
                      👁 查看無法匯入資料 ({previewData.invalid_rows_count} 筆)
                    </Button>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setPreviewData(null)}
                    disabled={committing}
                    className="text-xs"
                  >
                    取消
                  </Button>
                  <Button
                    onClick={handleCommitTxt}
                    loading={committing}
                    disabled={committing}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-6 py-2.5 shadow-md"
                  >
                    ✓ 確認匯入
                  </Button>
                </div>
              </div>
            </Card>
          ) : null}
        </div>
      )}

      {/* TAB 3: 資料待確認中心 5 大分類 (Section 二十三 & 二十四) */}
      {activeTab === 'quality' && (
        <div className="space-y-6">
          {/* 5 個清晰分類頁籤 */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { id: 'price_changes', label: '📈 價格異動', count: qualitySummary.price_changes || 0 },
              { id: 'duplicates', label: '🔀 疑似重複', count: qualitySummary.duplicates || 0 },
              { id: 'category_issues', label: '📁 商品分類', count: qualitySummary.category_issues || 0 },
              { id: 'barcode_issues', label: '🏷 條碼問題', count: qualitySummary.barcode_issues || 0 },
              { id: 'other_issues', label: '⚠️ 其他異常', count: qualitySummary.other_issues || 0 },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setQualityActiveTab(tab.id);
                  void loadQualityCenter(tab.id);
                }}
                className={`p-3 rounded-xl border text-center transition ${
                  qualityActiveTab === tab.id
                    ? 'bg-teal-700 text-white font-bold border-teal-700 shadow'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <span className="text-xs block">{tab.label}</span>
                <span
                  className={`text-lg font-extrabold mt-1 block ${
                    qualityActiveTab === tab.id ? 'text-teal-200' : 'text-teal-800'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* 批次操作工具列 (Section 二十四) */}
          {qualityItems.length > 0 && (
            <div className="flex flex-wrap justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-200 text-xs">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-700">
                  <input
                    type="checkbox"
                    checked={selectedIds.length > 0 && selectedIds.length === qualityItems.length}
                    onChange={handleToggleSelectAll}
                  />
                  全選本頁 ({selectedIds.length} / {qualityItems.length})
                </label>
              </div>

              <div className="flex gap-2">
                {qualityActiveTab === 'price_changes' && (
                  <Button
                    size="sm"
                    disabled={selectedIds.length === 0}
                    loading={batchActionLoading}
                    onClick={() => handleBatchResolve('ACCEPT_NEW_PRICE')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                  >
                    ✓ 批次接受新價格 ({selectedIds.length})
                  </Button>
                )}
                {qualityActiveTab === 'duplicates' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={selectedIds.length === 0}
                    loading={batchActionLoading}
                    onClick={() => handleBatchResolve('CONFIRM_DIFFERENT')}
                    className="text-xs"
                  >
                    ✓ 批次確認為不同商品 ({selectedIds.length})
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={selectedIds.length === 0}
                  loading={batchActionLoading}
                  onClick={() => handleBatchResolve('DISMISS')}
                  className="text-xs text-slate-500"
                >
                  標示稍後處理
                </Button>
              </div>
            </div>
          )}

          {qualityLoading ? (
            <Loading label="載入待確認清單中…" />
          ) : qualityItems.length === 0 ? (
            <Card className="p-8 text-center bg-slate-50">
              <EmptyState title="目前此分類無待確認項目" description="所有相關品項皆已確認完畢。" />
            </Card>
          ) : qualityActiveTab === 'price_changes' ? (
            /* 1. 價格異動 */
            <Card className="p-6">
              <h3 className="text-base font-bold text-ink mb-4">📈 價格異動審核</h3>
              <Table>
                <thead>
                  <tr className="bg-slate-50 text-slate-600 text-xs">
                    <th className="p-3 w-10">選取</th>
                    <th className="p-3">商品代號</th>
                    <th className="p-3">商品名稱</th>
                    <th className="p-3">目前進價</th>
                    <th className="p-3">VIP售價</th>
                    <th className="p-3">異動狀態</th>
                    <th className="p-3 text-right">採購操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {qualityItems.map((p) => (
                    <tr key={p.id}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(p.id)}
                          onChange={() => handleToggleSelect(p.id)}
                        />
                      </td>
                      <td className="p-3 font-mono font-bold text-teal-800">{p.product_code}</td>
                      <td className="p-3 font-bold text-ink">{p.product_name}</td>
                      <td className="p-3 font-bold">
                        <Money value={p.current_purchase_price} />
                      </td>
                      <td className="p-3 text-teal-700 font-bold">
                        <Money value={p.vip_price} />
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                            p.price_comparison_status === 'ZERO_PRICE_REVIEW'
                              ? 'bg-amber-100 text-amber-800'
                              : p.price_comparison_status === 'PRICE_ABNORMAL'
                                ? 'bg-rose-100 text-rose-800'
                                : 'bg-sky-100 text-sky-800'
                          }`}
                        >
                          {p.price_comparison_status === 'ZERO_PRICE_REVIEW'
                            ? '新來源進價為0(已保護原價)'
                            : p.price_comparison_status === 'PRICE_ABNORMAL'
                              ? '價格異常(進價高於售價)'
                              : '價格異動'}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button
                            size="sm"
                            onClick={() => handleResolvePriceReview(p.id, 'ACCEPT_NEW_PRICE', p.current_purchase_price)}
                            className="text-xs bg-emerald-600 hover:bg-emerald-700"
                          >
                            ✓ 接受價格
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleResolvePriceReview(p.id, 'RETAIN_OLD_PRICE')}
                            className="text-xs"
                          >
                            保留原價
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          ) : qualityActiveTab === 'duplicates' ? (
            /* 2. 疑似重複 */
            <Card className="p-6">
              <h3 className="text-base font-bold text-ink mb-4">🔀 疑似重複商品審核</h3>
              <Table>
                <thead>
                  <tr className="bg-slate-50 text-slate-600 text-xs">
                    <th className="p-3 w-10">選取</th>
                    <th className="p-3">相似度</th>
                    <th className="p-3">商品 A</th>
                    <th className="p-3">商品 B</th>
                    <th className="p-3">判定依據</th>
                    <th className="p-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {qualityItems.map((c) => (
                    <tr key={c.id}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(c.id)}
                          onChange={() => handleToggleSelect(c.id)}
                        />
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-1 bg-rose-100 text-rose-800 font-extrabold rounded text-xs">
                          {c.similarity_score}%
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="font-mono font-bold text-teal-800">{c.pa_code}</div>
                        <div className="font-bold text-ink">{c.pa_name}</div>
                        <div className="text-[11px] text-slate-500">進價: {c.pa_in_price} ｜ 單位: {c.pa_unit}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-mono font-bold text-purple-800">{c.pb_code}</div>
                        <div className="font-bold text-ink">{c.pb_name}</div>
                        <div className="text-[11px] text-slate-500">進價: {c.pb_in_price} ｜ 單位: {c.pb_unit}</div>
                      </td>
                      <td className="p-3 text-slate-500">{c.match_reasons_json}</td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleResolveDuplicate(c.id, 'SET_SAME_FAMILY')}
                            className="text-xs bg-teal-50 text-teal-800"
                          >
                            👨‍👩‍👧 設為家族
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleResolveDuplicate(c.id, 'CONFIRM_DIFFERENT')}
                            className="text-xs"
                          >
                            ✓ 不同商品
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          ) : (
            /* 3. 分類 / 條碼 / 其他異常清單 */
            <Card className="p-6">
              <h3 className="text-base font-bold text-ink mb-4">待確認項目清單</h3>
              <Table>
                <thead>
                  <tr className="bg-slate-50 text-slate-600 text-xs">
                    <th className="p-3 w-10">選取</th>
                    <th className="p-3">商品代號</th>
                    <th className="p-3">商品名稱</th>
                    <th className="p-3">大分類 / 子分類</th>
                    <th className="p-3">目前進價</th>
                    <th className="p-3">問題註記</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {qualityItems.map((p) => (
                    <tr key={p.id}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(p.id)}
                          onChange={() => handleToggleSelect(p.id)}
                        />
                      </td>
                      <td className="p-3 font-mono font-bold text-teal-800">{p.product_code}</td>
                      <td className="p-3 font-bold text-ink">{p.product_name}</td>
                      <td className="p-3">
                        {p.product_category_code ? `${p.product_category_code} ${HUI_SHENG_CATEGORY_MAP[p.product_category_code] || ''}` : '未設定'}
                      </td>
                      <td className="p-3 font-bold">
                        <Money value={p.current_purchase_price} />
                      </td>
                      <td className="p-3 text-amber-700">
                        {p.data_quality_issues_json || '待確認'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
        </div>
      )}

      {/* TAB 4: 匯入紀錄與 Rollback */}
      {activeTab === 'history' && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-base font-bold text-ink">歷史 TXT 匯入批次紀錄與 Rollback</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                管理員可於此檢視每次匯入之檔案 Hash、進價稅別與筆數統計，並支援一鍵回復特定批次（Rollback）。
              </p>
            </div>
          </div>
          {batchesLoading ? (
            <Loading label="載入匯入紀錄中…" />
          ) : batches.length === 0 ? (
            <EmptyState title="尚無匯入紀錄" description="匯入 TXT 檔案後，歷史批次將記錄於此。" />
          ) : (
            <Table>
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-xs">
                  <th className="p-3">批次編號</th>
                  <th className="p-3">檔案名稱</th>
                  <th className="p-3">進價稅別</th>
                  <th className="p-3">總筆數</th>
                  <th className="p-3">新增 / 更新 / 無變更</th>
                  <th className="p-3">狀態</th>
                  <th className="p-3">匯入時間</th>
                  <th className="p-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {batches.map((b) => (
                  <tr key={b.id}>
                    <td className="p-3 font-mono font-bold text-teal-800">{b.batch_no}</td>
                    <td className="p-3 font-medium text-ink">{b.file_name}</td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[11px]">
                        {b.purchase_price_tax_type === 'TAX_EXCLUSIVE' ? '未稅' : '含稅'}
                      </span>
                    </td>
                    <td className="p-3 font-bold">{b.total_rows}</td>
                    <td className="p-3">
                      <span className="text-emerald-700 font-bold">+{b.inserted_rows}</span> /{' '}
                      <span className="text-sky-700 font-bold">~{b.updated_rows}</span> /{' '}
                      <span className="text-slate-400">{b.skipped_rows}</span>
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          b.status === 'ROLLED_BACK'
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {b.status === 'ROLLED_BACK' ? '已回復 (Rolled Back)' : '正常完成'}
                      </span>
                    </td>
                    <td className="p-3 text-slate-400 text-[11px]">{b.created_at}</td>
                    <td className="p-3 text-right">
                      {b.status !== 'ROLLED_BACK' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRollbackBatch(b.id, b.batch_no)}
                          className="text-xs text-rose-600 hover:bg-rose-50"
                        >
                          ↩ 回復本次匯入
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {/* 編輯自訂欄位 Modal */}
      {editingItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-ink">
                編輯商品人工補充欄位：{editingItem.product_name}
              </h3>
              <button
                onClick={() => setEditingItem(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  目前廠商搭贈條件（如：買12送1、買10送2）
                </label>
                <input
                  type="text"
                  className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                  placeholder="例：買12送1"
                  value={giveawayTerms}
                  onChange={(e) => setGiveawayTerms(e.target.value)}
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  ＊此欄位為採購專屬維護，後續 TXT 重新匯入絕不會被覆蓋清空。
                </p>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  商品成分結構化資料 (JSON 格式)
                </label>
                <textarea
                  className="w-full h-24 p-2.5 font-mono border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                  placeholder='[{"name": "維生素C", "amount": 1000, "unit": "mg"}]'
                  value={ingredientsJson}
                  onChange={(e) => setIngredientsJson(e.target.value)}
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">所屬商品家族 (同商品不同包裝規格)</label>
                <Select value={familyId} onChange={(e) => setFamilyId(e.target.value)}>
                  <option value="">未歸入家族</option>
                  {families.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.family_name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button variant="ghost" onClick={() => setEditingItem(null)}>
                取消
              </Button>
              <Button onClick={saveCustomFields} loading={savingCustom}>
                儲存設定
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 查看無法匯入資料 Modal (Section 二十五) */}
      {showInvalidModal && previewData && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-rose-900">
                  無法匯入商品清單（共 {previewData.invalid_rows_count} 筆）
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  以下列因缺少必要之商品代號或商品名稱，無法建立識別主檔。
                </p>
              </div>
              <button
                onClick={() => setShowInvalidModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="overflow-x-auto max-h-80 border border-slate-200 rounded-xl">
              <Table>
                <thead>
                  <tr className="bg-slate-50 text-slate-600 text-xs">
                    <th className="p-3">來源資料列</th>
                    <th className="p-3">商品代號</th>
                    <th className="p-3">商品名稱</th>
                    <th className="p-3">錯誤原因</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {previewData.invalid_rows.map((r, idx) => (
                    <tr key={idx}>
                      <td className="p-3 font-mono font-bold text-slate-600">第 {r.rowNumber} 列</td>
                      <td className="p-3 font-mono text-rose-700">{r.itemId || '（空白）'}</td>
                      <td className="p-3 text-slate-700">{r.cname || '（空白）'}</td>
                      <td className="p-3 text-rose-700 font-bold">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-100">
              <Button onClick={() => setShowInvalidModal(false)}>
                關閉
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 批次自動補全成分 Modal */}
      {showBatchEnrichModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-teal-950 flex items-center gap-2">
                  <span>⚡</span> 批次自動補全商品成分資料
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  依商品身分與分類，自動檢索官方許可證快取及公開可信來源，分批平穩處理。
                </p>
              </div>
              <button
                onClick={() => setShowBatchEnrichModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-1 flex-1">
              <div className="grid grid-cols-2 gap-3 p-4 bg-teal-50/50 rounded-xl border border-teal-100 text-xs">
                <div>
                  <span className="text-slate-500">待補全缺成分總數：</span>
                  <span className="font-bold text-teal-800 text-sm block mt-0.5">
                    {enrichmentSummary ? enrichmentSummary.missingIngredients.toLocaleString() : '—'} 筆
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">已確認成分商品：</span>
                  <span className="font-bold text-emerald-700 text-sm block mt-0.5">
                    {enrichmentSummary ? enrichmentSummary.confirmed.toLocaleString() : '—'} 筆
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-bold text-slate-700">指定大分類（選填，預設全庫缺成分商品）：</label>
                <Select value={batchCategory} onChange={(e) => setBatchCategory(e.target.value)}>
                  <option value="">全部大分類 (A~P)</option>
                  {Object.entries(HUI_SHENG_CATEGORY_MAP).map(([code, name]) => (
                    <option key={code} value={code}>
                      {code} - {name}
                    </option>
                  ))}
                </Select>
              </div>

              {batchEnriching && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-center space-y-2">
                  <div className="animate-spin text-2xl">⚙️</div>
                  <div className="text-xs font-bold text-teal-800">{batchEnrichProgress}</div>
                  <div className="text-[11px] text-slate-400">
                    系統分批檢索中，請勿關閉視窗…
                  </div>
                </div>
              )}

              {batchEnrichResults.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-slate-700">本次批次處理結果：</h4>
                  <div className="border border-slate-200 rounded-xl overflow-hidden max-h-56 overflow-y-auto text-xs">
                    <Table>
                      <thead>
                        <tr className="bg-slate-50 text-slate-600">
                          <th className="p-2">商品</th>
                          <th className="p-2">狀態</th>
                          <th className="p-2">信心度</th>
                          <th className="p-2">查得來源</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {batchEnrichResults.map((r, idx) => (
                          <tr key={idx}>
                            <td className="p-2">
                              <span className="font-bold text-ink">{r.productName}</span>
                              <span className="text-[10px] text-slate-400 block">{r.productCode}</span>
                            </td>
                            <td className="p-2">
                              <span
                                className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                  r.status === 'FOUND_UNCONFIRMED'
                                    ? 'bg-amber-50 text-amber-800'
                                    : r.status === 'CONFIRMED'
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-slate-100 text-slate-500'
                                }`}
                              >
                                {r.status}
                              </span>
                            </td>
                            <td className="p-2 font-bold text-teal-700">{r.score}%</td>
                            <td className="p-2 text-slate-600 truncate max-w-[140px]" title={r.sourceName}>
                              {r.sourceName || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button variant="ghost" onClick={() => setShowBatchEnrichModal(false)}>
                關閉
              </Button>
              <Button
                onClick={() => void handleBatchEnrich()}
                loading={batchEnriching}
                className="bg-teal-700"
              >
                ⚡ 開始自動補全 (每批50筆)
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 候選成分來源與明細抽屜 Modal */}
      {selectedCandidateProduct && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-start border-b border-slate-100 pb-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded font-mono text-xs font-bold">
                    {selectedCandidateProduct.product_code}
                  </span>
                  <h3 className="text-base font-bold text-ink">
                    {selectedCandidateProduct.product_name}
                  </h3>
                </div>
                <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
                  <span>規格：{selectedCandidateProduct.specification || selectedCandidateProduct.sales_unit || '未設定'}</span>
                  <span>條碼：{selectedCandidateProduct.primary_barcode_candidate || '無'}</span>
                  <span>分類：{selectedCandidateProduct.product_category_code || '—'}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedCandidateProduct(null);
                  setCandidateDrawerData(null);
                }}
                className="text-slate-400 hover:text-slate-600 text-lg"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-1 flex-1">
              {loadingCandidates ? (
                <Loading label="讀取成分候選來源中…" />
              ) : !candidateDrawerData || candidateDrawerData.sources.length === 0 ? (
                <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200 space-y-3">
                  <div className="text-3xl">🔍</div>
                  <h4 className="text-sm font-bold text-slate-700">目前尚無查得之成分候選資料</h4>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto">
                    可點擊下方「立即查找成分」依國際條碼與官方藥品許可證資料庫進行即時比對。
                  </p>
                  <Button
                    onClick={() => void handleSingleEnrich(selectedCandidateProduct.id)}
                    loading={enrichingSingleId === selectedCandidateProduct.id}
                    className="bg-teal-700"
                  >
                    🔍 立即查找成分
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {candidateDrawerData.sources.map((src) => {
                    const isConfirmed = src.status === 'CONFIRMED';
                    const isRejected = src.status === 'REJECTED';
                    const srcLevelTag =
                      src.source_type === 'A_OFFICIAL_GOV'
                        ? 'TFDA 官方政府資料'
                        : src.source_type === 'A_BRAND_OFFICIAL'
                        ? '品牌/製造商官方'
                        : src.source_type === 'B_RETAILER'
                        ? '公開零售通路'
                        : src.source_type === 'MANUAL'
                        ? '採購人工維護'
                        : '其他公開來源';

                    return (
                      <div
                        key={src.id}
                        className={`p-4 rounded-xl border transition ${
                          isConfirmed
                            ? 'border-emerald-500 bg-emerald-50/20 ring-2 ring-emerald-500/20'
                            : isRejected
                            ? 'border-slate-200 bg-slate-50/60 opacity-60'
                            : 'border-slate-200 bg-white hover:border-teal-300'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                  src.source_type === 'A_OFFICIAL_GOV'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : src.source_type === 'A_BRAND_OFFICIAL'
                                    ? 'bg-sky-100 text-sky-800'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                [{srcLevelTag}]
                              </span>
                              <span className="font-bold text-sm text-ink">{src.source_name}</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-2">
                              <span>比對方式：{src.matched_by}</span>
                              {src.match_value && <span>({src.match_value})</span>}
                              <span>完整度：{src.completeness}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 text-xs font-bold">
                              信心度 {src.confidence_score}%
                            </span>
                            {isConfirmed && (
                              <span className="block mt-1 text-[10px] text-emerald-700 font-bold">
                                ✓ 目前正式採納來源
                              </span>
                            )}
                            {isRejected && (
                              <span className="block mt-1 text-[10px] text-rose-600 font-bold">
                                ✕ 已排除
                              </span>
                            )}
                          </div>
                        </div>

                        {/* 原始成分文字 */}
                        <div className="mt-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100 text-xs text-slate-700">
                          <span className="font-bold text-slate-500 block mb-1">成分內容原文：</span>
                          {src.raw_ingredient_text || '無詳細文字'}
                        </div>

                        {/* 結構化清單 */}
                        {candidateDrawerData.ingredients.filter((i) => i.source_id === src.id).length > 0 && (
                          <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden text-xs">
                            <Table>
                              <thead>
                                <tr className="bg-slate-50 text-slate-600">
                                  <th className="p-2">成分名稱</th>
                                  <th className="p-2">標準化識別</th>
                                  <th className="p-2 text-right">含量 / 單位</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {candidateDrawerData.ingredients
                                  .filter((i) => i.source_id === src.id)
                                  .map((ing) => (
                                    <tr key={ing.id}>
                                      <td className="p-2 font-bold text-ink">{ing.ingredient_name_raw}</td>
                                      <td className="p-2 font-mono text-teal-700 text-[11px]">{ing.ingredient_name_normalized}</td>
                                      <td className="p-2 text-right font-medium">
                                        {ing.amount !== null ? `${ing.amount} ${ing.unit || ''}` : '未載明含量'}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </div>
                        )}

                        {/* 操作按鈕 */}
                        {!isRejected && (
                          <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end gap-2 text-xs">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => void handleRejectSource(src.id)}
                              loading={rejectingSourceId === src.id}
                              className="text-rose-600 hover:bg-rose-50"
                            >
                              ✕ 排除此來源
                            </Button>
                            {!isConfirmed && (
                              <Button
                                size="sm"
                                onClick={() => void handleConfirmSource(src.id)}
                                loading={confirmingSourceId === src.id}
                                className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold"
                              >
                                ✓ 確認採納此來源為正式商品成分
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-slate-100">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleSingleEnrich(selectedCandidateProduct.id)}
                loading={enrichingSingleId === selectedCandidateProduct.id}
              >
                🔄 重新查找成分
              </Button>
              <Button
                onClick={() => {
                  setSelectedCandidateProduct(null);
                  setCandidateDrawerData(null);
                }}
              >
                關閉
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
